/**
 * HTTP-Anbindung an die API.
 *
 * Kümmert sich um Token, automatische Erneuerung abgelaufener Sitzungen und
 * einheitliche Fehlerobjekte. Die Oberfläche muss sich damit nirgends mit
 * Statuscodes befassen.
 */
import type {
  AnnualReport,
  BgpNeighbor,
  BgpSession,
  ChronicleEntry,
  Command,
  Competitor,
  CommandResult,
  CompanyValue,
  Customer,
  Explanation,
  HistoryTotals,
  EconomyState,
  LoanProductTemplate,
  MonthlyRecord,
  NewsItem,
  ObjectiveView,
  PartKind,
  PartModel,
  ProgressSummary,
  RecordEntry,
  Regime,
  RegionFactors,
  RegionTrend,
  RoutingTable,
  TechWave,
  TrendState,
  World,
} from '@hosting-sim/core';

const BASE = '/api';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  /**
   * Rolle auf der Plattform, nicht in der Partie. Wer in einer Partie was darf,
   * hängt an der Mitgliedschaft und entscheidet sich im Backend.
   */
  platformRole: 'USER' | 'STAFF' | 'OWNER';
  /**
   * Steht die hinterlegte Adresse auf der Wegwerfliste? Betrifft nur Konten
   * von vor der Registrierungsprüfung. `RequireAuth` blockt das Dashboard,
   * bis eine echte Adresse bestätigt ist – siehe `EmailReplacementGate`.
   */
  emailNeedsReplacement: boolean;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

/** Antwort der Registrierung – bewusst ohne Token, siehe `api.register`. */
export interface RegisterResult {
  email: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const STORAGE_KEY = 'hosting-sim.auth';

interface StoredAuth {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

export function loadStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredAuth) : null;
  } catch {
    return null;
  }
}

export function storeAuth(auth: StoredAuth | null): void {
  if (auth) localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  else localStorage.removeItem(STORAGE_KEY);
}

/** Läuft gerade eine Token-Erneuerung? Verhindert parallele Refresh-Aufrufe. */
let refreshing: Promise<string | null> | null = null;

/**
 * Wird aufgerufen, wenn eine Sitzung endgültig für ungültig erklärt wurde – nach
 * gescheiterter Token-Erneuerung.
 *
 * `api.ts` kennt den Anmeldezustand (`store/auth.ts`) nicht, weil dieser
 * umgekehrt `api.ts` importiert – ein direkter Import hier wäre ein Zirkel.
 * Der Store trägt sich stattdessen über `onAuthExpired` ein und räumt sein
 * `user`-Feld selbst ab. Ohne das blieb `RequireAuth` beim zuletzt bekannten
 * Benutzer stehen: Die Oberfläche sah angemeldet aus, obwohl jede Anfrage –
 * auch "Neue Partie" – nur noch mit 401 scheiterte, und es gab keinen Weg
 * zurück zur Anmeldung außer manuellem Abmelden.
 */
let authExpiredHandler: (() => void) | null = null;
export function onAuthExpired(handler: () => void): void {
  authExpiredHandler = handler;
}

export async function refreshAccessToken(): Promise<string | null> {
  const stored = loadStoredAuth();
  if (!stored?.refreshToken) return null;

  refreshing ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: stored.refreshToken }),
      });
      if (!response.ok) {
        storeAuth(null);
        authExpiredHandler?.();
        return null;
      }
      const data = (await response.json()) as AuthResponse;
      storeAuth(data);
      return data.accessToken;
    } finally {
      // Nächster Aufruf darf wieder erneuern.
      setTimeout(() => (refreshing = null), 0);
    }
  })();

  return refreshing;
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const stored = loadStoredAuth();
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
  if (stored?.accessToken) headers.set('Authorization', `Bearer ${stored.accessToken}`);

  const response = await fetch(`${BASE}${path}`, { ...init, headers });

  // Abgelaufenes Access-Token: einmal erneuern und den Aufruf wiederholen.
  if (response.status === 401 && retry) {
    const token = await refreshAccessToken();
    if (token) return request<T>(path, init, false);
  }

  if (!response.ok) {
    let message = `Fehler ${response.status}`;
    try {
      const body = (await response.json()) as { message?: string | string[] };
      message = Array.isArray(body.message) ? body.message.join(', ') : (body.message ?? message);
    } catch {
      /* Antwort ohne JSON-Körper */
    }
    throw new ApiError(message, response.status);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// Endpunkte
// ---------------------------------------------------------------------------

export interface GameListEntry {
  id: string;
  name: string;
  companyName: string;
  difficulty: string;
  running: boolean;
  speed: number;
  updatedAt: string;
  isOwner: boolean;
  saves: {
    slot: number;
    label: string;
    tick: number;
    gameDate: string;
    cash: number;
    customers: number;
    companyValue: number;
    updatedAt: string;
  }[];
}

export interface ChatMessageEntry {
  id: string;
  channel: string;
  authorId: string;
  displayName: string;
  body: string;
  createdAt: string;
}

export interface LeaderboardEntry {
  rank: number;
  gameId: string;
  name: string;
  companyName: string;
  /** Nur bei der globalen Liste gesetzt, nicht beim eigenen Rang. */
  ownerDisplayName?: string;
  companyValue: number;
  cash: number;
  customers: number;
}

// ---------------------------------------------------------------------------
// Verwaltung: fremde Partien und ihre simulierten Kunden
// ---------------------------------------------------------------------------

export interface AdminGameListEntry {
  id: string;
  name: string;
  companyName: string;
  difficulty: string;
  running: boolean;
  speed: number;
  updatedAt: string;
  ownerEmail: string;
  ownerDisplayName: string;
  cash: number | null;
  customers: number | null;
  companyValue: number | null;
  tick: number | null;
}

export interface AdminGameDetail {
  game: {
    id: string;
    name: string;
    companyName: string;
    difficulty: string;
    running: boolean;
    speed: number;
    createdAt: string;
    updatedAt: string;
  };
  owner: { id: string; displayName: string; email: string };
  members: { id: string; displayName: string; email: string; role: GameRole }[];
  world: {
    meta: World['meta'];
    company: World['company'];
    counts: { customers: number; contracts: number; servers: number };
  };
}

export interface AdminCustomerEntry {
  id: string;
  name: string;
  company: string | null;
  email: string;
  segment: string;
  satisfaction: number;
  balance: number;
  churnRisk: number;
  reliability: number;
  activeContracts: number;
  openTickets: number;
}

export interface AdminCustomerDetail {
  customer: Customer;
  contracts: { id: string; productName: string; monthlyPrice: number; state: string }[];
  openTickets: { id: string; subject: string; priority: string; state: string }[];
}

/** Editierbare Felder eines Kunden über das Verwaltungskommando. */
export type AdminCustomerPatch = Partial<{
  name: string;
  company: string | null;
  email: string;
  segment: string;
  satisfaction: number;
  balance: number;
  churnRisk: number;
  reliability: number;
  notes: string;
}>;

/** Editierbare Felder des Unternehmens über das Verwaltungskommando. */
export type AdminCompanyPatch = Partial<{ name: string; cash: number; debt: number; reputation: number; creditLimit: number }>;

/** Rolle innerhalb einer Partie – wer im gespielten Unternehmen was darf. */
export type GameRole = 'ADMIN' | 'TECHNICIAN' | 'SUPPORT' | 'CUSTOMER';

export interface GameMemberEntry {
  id: string;
  email: string;
  displayName: string;
  role: GameRole;
  roleLabel: string;
  joinedAt?: string;
}

export interface GameMembers {
  owner: GameMemberEntry;
  members: GameMemberEntry[];
}

export interface GameSummary {
  meta: World['meta'];
  company: World['company'];
  derived: World['derived'];
  tickets: { open: number; urgent: number; overdue: number; avgFirstResponseTicks: number; resolvedLast24h: number };
  ipv4: { totalAddresses: number; usedAddresses: number; prefixCount: number; announcedPrefixes: number; monthlyCost: number };
  ipv6: { totalAddresses: number; usedAddresses: number; prefixCount: number; announcedPrefixes: number; monthlyCost: number };
  counts: Record<string, number>;
}

export const api = {
  // Authentifizierung
  //
  // `register` liefert kein Tokenpaar: Angemeldet wird erst nach der
  // Bestätigung über den Link aus der E-Mail.
  register: (body: { email: string; password: string; displayName: string }) =>
    request<RegisterResult>('/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (body: { email: string; password: string }) =>
    request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  logout: (refreshToken: string) =>
    request<void>('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }),
  verifyEmail: (token: string) => request<AuthResponse>('/auth/verify', { method: 'POST', body: JSON.stringify({ token }) }),
  resendVerification: (email: string) =>
    request<void>('/auth/resend-verification', { method: 'POST', body: JSON.stringify({ email }) }),
  forgotPassword: (email: string) =>
    request<void>('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string) =>
    request<AuthResponse>('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
  requestEmailChange: (email: string) => request<void>('/auth/change-email', { method: 'POST', body: JSON.stringify({ email }) }),

  // Adminpanel
  listUsers: () =>
    request<
      {
        id: string;
        email: string;
        displayName: string;
        platformRole: string;
        emailVerifiedAt: string | null;
        createdAt: string;
        lastLoginAt: string | null;
        gamesCount: number;
      }[]
    >('/auth/users'),
  setUserRole: (id: string, role: string) =>
    request<{ ok: boolean }>(`/auth/users/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  deleteUser: (id: string) => request<{ ok: boolean }>(`/auth/users/${id}`, { method: 'DELETE' }),

  // Partien
  listGames: () => request<GameListEntry[]>('/games'),
  createGame: (body: { name: string; companyName: string; difficulty: string; seed?: number; startLocation?: string }) =>
    request<{ gameId: string }>('/games', { method: 'POST', body: JSON.stringify(body) }),
  deleteGame: (id: string) => request<{ ok: boolean }>(`/games/${id}`, { method: 'DELETE' }),

  // Mitglieder
  listMembers: (id: string) => request<GameMembers>(`/games/${id}/members`),
  inviteMember: (id: string, email: string, role: GameRole) =>
    request<GameMemberEntry>(`/games/${id}/members`, { method: 'POST', body: JSON.stringify({ email, role }) }),
  removeMember: (id: string, userId: string) =>
    request<{ ok: boolean }>(`/games/${id}/members/${userId}`, { method: 'DELETE' }),

  // Zustand
  myRole: (id: string) => request<{ role: GameRole; roleLabel: string; canControlTime: boolean }>(`/games/${id}/role`),
  state: (id: string) => request<World>(`/games/${id}/state`),
  summary: (id: string) => request<GameSummary>(`/games/${id}/summary`),
  command: (id: string, command: Command) =>
    request<CommandResult>(`/games/${id}/command`, { method: 'POST', body: JSON.stringify({ command }) }),
  setRunning: (id: string, running: boolean, speed?: number) =>
    request<{ ok: boolean }>(`/games/${id}/running`, { method: 'POST', body: JSON.stringify({ running, speed }) }),
  fastForward: (id: string, ticks: number) =>
    request<{ ticks: number; newCustomers: number; lostCustomers: number; events: string[] }>(`/games/${id}/fast-forward`, {
      method: 'POST',
      body: JSON.stringify({ ticks }),
    }),

  // Teilansichten
  routing: (id: string, routerId: string) =>
    request<{
      router: { id: string; name: string; asn: number; routerId: string };
      table: RoutingTable;
      sessions: BgpSession[];
      neighbors: BgpNeighbor[];
    }>(`/games/${id}/routing/${routerId}`),
  logs: (id: string, params: { category?: string; severity?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.severity) query.set('severity', params.severity);
    if (params.limit) query.set('limit', String(params.limit));
    return request<World['logs']>(`/games/${id}/logs?${query.toString()}`);
  },
  ledger: (id: string, limit = 300) => request<World['ledger']>(`/games/${id}/ledger?limit=${limit}`),
  catalog: (id: string) =>
    request<{
      servers: Record<string, unknown>[];
      /** Leere Gehäuse für den Konfigurator. */
      chassis: Record<string, unknown>[];
      routers: Record<string, unknown>[];
      switches: Record<string, unknown>[];
      firewalls: Record<string, unknown>[];
      loadbalancers: Record<string, unknown>[];
      ups: Record<string, unknown>[];
      loanProducts: LoanProductTemplate[];
      racks: Record<string, unknown>[];
      locations: Record<string, unknown>[];
      /** Einzeln bestellbare Bauteile, nach Art gruppiert. */
      parts: Record<PartKind, PartModel[]>;
    }>(`/games/${id}/catalog`),
  advisor: (id: string, topic?: string) =>
    request<{ alerts: World['derived']['alerts']; analysis: { question: string; answer: string; details: string[] } | null }>(
      `/games/${id}/advisor${topic ? `?topic=${topic}` : ''}`,
    ),
  objectives: (id: string) =>
    request<{ objectives: ObjectiveView[]; summary: ProgressSummary }>(`/games/${id}/objectives`),

  // Auswertung
  explain: (id: string, metric: string, windowTicks?: number) =>
    request<Explanation>(`/games/${id}/explain?metric=${metric}${windowTicks ? `&window=${windowTicks}` : ''}`),
  history: (id: string) =>
    request<{
      months: MonthlyRecord[];
      chronicle: ChronicleEntry[];
      years: number[];
      totals: HistoryTotals;
      value: CompanyValue;
    }>(`/games/${id}/history`),
  historyRecords: (id: string) => request<{ records: RecordEntry[] }>(`/games/${id}/history/records`),

  // Die Welt außerhalb des Unternehmens
  world: (id: string) =>
    request<{
      economy: EconomyState;
      regime: Regime;
      trends: TrendState;
      waves: { key: string; startedAtTick: number; endsAtTick: number; wave?: TechWave }[];
      regions: Record<string, RegionFactors>;
      regionTrends: { key: string; startedAtTick: number; endsAtTick: number; trend?: RegionTrend }[];
    }>(`/games/${id}/world`),
  competitors: (id: string) =>
    request<{
      firms: Competitor[];
      priceLevel: Record<string, number>;
      occupied: Record<string, number>;
      own: Record<string, number>;
    }>(`/games/${id}/competitors`),
  news: (id: string, category?: string, limit = 80) =>
    request<{ items: NewsItem[] }>(`/games/${id}/news?limit=${limit}${category ? `&category=${category}` : ''}`),
  annualReport: (id: string, year: number) => request<AnnualReport>(`/games/${id}/history/annual/${year}`),

  // Spielstände
  save: (id: string, slot: number, label: string) =>
    request<{ ok: boolean }>(`/games/${id}/save`, { method: 'POST', body: JSON.stringify({ slot, label }) }),
  load: (id: string, slot: number) => request<{ ok: boolean }>(`/games/${id}/load/${slot}`, { method: 'POST' }),
  exportSave: (id: string) => request<Record<string, unknown>>(`/games/${id}/export`),
  importSave: (id: string, snapshot: Record<string, unknown>) =>
    request<{ ok: boolean }>(`/games/${id}/import`, { method: 'POST', body: JSON.stringify({ snapshot }) }),

  // Chat
  lobbyChat: (before?: string) => request<ChatMessageEntry[]>(`/chat/lobby${before ? `?before=${before}` : ''}`),
  gameChat: (id: string, before?: string) =>
    request<ChatMessageEntry[]>(`/chat/game/${id}${before ? `?before=${before}` : ''}`),

  // Bestenliste
  leaderboard: (limit = 20) => request<LeaderboardEntry[]>(`/leaderboard?limit=${limit}`),
  myRank: () => request<LeaderboardEntry[]>('/leaderboard/me'),

  // Verwaltung: fremde Partien und ihre simulierten Kunden (nur Inhaber)
  adminListGames: (query?: string, take = 50, skip = 0) =>
    request<{ total: number; games: AdminGameListEntry[] }>(
      `/admin/games?take=${take}&skip=${skip}${query ? `&query=${encodeURIComponent(query)}` : ''}`,
    ),
  adminGameDetail: (id: string) => request<AdminGameDetail>(`/admin/games/${id}`),
  adminUpdateGame: (id: string, body: { name?: string; companyName?: string; running?: boolean; speed?: number }) =>
    request<{ ok: boolean }>(`/admin/games/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminDeleteGame: (id: string) => request<{ ok: boolean }>(`/admin/games/${id}`, { method: 'DELETE' }),
  adminListCustomers: (id: string, query?: string) =>
    request<{ customers: AdminCustomerEntry[] }>(`/admin/games/${id}/customers${query ? `?query=${encodeURIComponent(query)}` : ''}`),
  adminGetCustomer: (id: string, customerId: string) => request<AdminCustomerDetail>(`/admin/games/${id}/customers/${customerId}`),
  adminEditCustomer: (id: string, customerId: string, patch: AdminCustomerPatch) =>
    request<CommandResult>(`/admin/games/${id}/command`, {
      method: 'POST',
      body: JSON.stringify({ command: { type: 'admin/editCustomer', customerId, patch } }),
    }),
  adminEditCompany: (id: string, patch: AdminCompanyPatch) =>
    request<CommandResult>(`/admin/games/${id}/command`, {
      method: 'POST',
      body: JSON.stringify({ command: { type: 'admin/editCompany', patch } }),
    }),
};
